import { useMutation } from "@tanstack/react-query"
import { CheckCircle2, ScanText } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { ProgresoLectura } from "@/componentes/ordenes/ProgresoLectura"
import {
  type Ajustes,
  type Lectura,
  Lista,
  ResultadoLectura,
} from "@/componentes/ordenes/ResultadoLectura"
import {
  type Adjuntando,
  AdjuntosNuevos,
  archivosPegados,
  sumar,
} from "@/componentes/ui/AdjuntosNuevos"
import { Boton } from "@/componentes/ui/Boton"

/**
 * Leer una factura con IA y convertirla en una orden en BORRADOR, desde «Órdenes de pago».
 *
 * ⚠ **Dos pasos, y el segundo no existe sin el primero.** «Leer» enseña TODO lo que se
 * leyó y lo que la verificación dice de ello; «Crear» usa esa MISMA lectura —guardada
 * en el servidor 15 minutos—, no una nueva. Lo que se crea es lo que se vio.
 *
 * ⚠ **Los bloqueos no se saltan desde aquí.** Si los renglones no suman el total, la
 * orden diría un importe distinto del papel: el botón de crear no se ofrece. Lo que
 * queda es cargarla a mano con «Nueva orden», que es lo que había antes de esto.
 *
 * ⚠ **Dos clases de papel: factura o SOLICITUD DE FONDOS.** Una solicitud es la captura
 * de un mensaje —«110 $ a Binance» con cédula, teléfono y banco—, sin factura. El
 * monto, la moneda y el concepto salen del CONTEXTO si los dice; si el mensaje dice
 * otra cifra, no se crea. La orden nace sin documento fiscal (fuera del libro de
 * compras) y los datos de pago van a sus notas, nunca a la ficha del proveedor.
 *
 * ⚠ **La factura sale del sistema, a Google.** Lo dice la pantalla antes de leer, no un
 * aviso escondido: es la decisión que el permiso `factura_ia:usar` pone en manos de
 * quien lo tiene.
 */

type Borrador = { orden_id: number; codigo: string; total: string; avisos: string[] }

type Clase = "factura" | "solicitud"

export function LectorFactura({ onAbrir }: { onAbrir: (ordenId: number) => void }) {
  const [clase, setClase] = useState<Clase>("factura")
  const [pendientes, setPendientes] = useState<Adjuntando[]>([])
  const [rechazados, setRechazados] = useState<string[]>([])
  const [contexto, setContexto] = useState("")

  const leer = useMutation({
    mutationFn: () => {
      const datos = new FormData()
      for (const p of pendientes) datos.append("archivos", p.archivo)
      datos.append("contexto", contexto)
      return api.post<Lectura>(
        clase === "factura" ? "/api/v1/facturas-ia/leer" : "/api/v1/facturas-ia/solicitud",
        datos,
      )
    },
  })
  const crear = useMutation({
    meta: {
      exito: { titulo: "Borrador creado", texto: "Revísalo antes de enviarlo a aprobación." },
    },
    mutationFn: (datos: { lecturaId: string; ajustes: Ajustes; proveedorId: number | null }) =>
      api.post<Borrador>("/api/v1/facturas-ia/borrador", {
        lectura_id: datos.lecturaId,
        ajustes: datos.ajustes,
        proveedor_id: datos.proveedorId,
      }),
  })

  function recibir(archivos: File[]) {
    const siguiente = sumar(pendientes, archivos)
    setPendientes(siguiente.adjuntos)
    setRechazados(siguiente.rechazados)
    leer.reset()
  }

  const lectura = leer.data
  const creada = crear.data

  if (creada) {
    return (
      <div className="space-y-3">
        <p className="flex items-center gap-2 font-medium">
          <CheckCircle2 size={20} style={{ color: "var(--success)" }} aria-hidden={true} />
          {creada.codigo} creada en borrador por {creada.total}.
        </p>
        {creada.avisos.length > 0 && <Lista titulo="Revisa en la ficha" items={creada.avisos} />}
        <div className="flex justify-end">
          <Boton variante="primario" onClick={() => onAbrir(creada.orden_id)}>
            Abrir la orden
          </Boton>
        </div>
      </div>
    )
  }

  return (
    <div
      className="space-y-4"
      // El pegado se escucha en todo el lector: una captura con Ctrl+V es como llega
      // de verdad una factura de compra en línea.
      onPaste={(evento) => {
        const archivos = archivosPegados(evento.clipboardData, pendientes.length)
        if (archivos.length === 0) return
        evento.preventDefault()
        recibir(archivos)
      }}
    >
      <fieldset className="flex flex-wrap gap-4 text-sm">
        <legend className="sr-only">Qué se va a leer</legend>
        {(
          [
            ["factura", "Factura o nota de entrega"],
            ["solicitud", "Solicitud de fondos (mensaje, sin factura)"],
          ] as const
        ).map(([valor, etiqueta]) => (
          <label key={valor} className="flex items-center gap-2">
            <input
              type="radio"
              name="clase-lectura"
              checked={clase === valor}
              onChange={() => {
                setClase(valor)
                leer.reset()
              }}
            />
            {etiqueta}
          </label>
        ))}
      </fieldset>
      <AdjuntosNuevos
        adjuntos={pendientes}
        rechazados={rechazados}
        onArchivos={recibir}
        etiqueta={
          clase === "factura"
            ? "Adjuntar la factura (foto, captura o PDF)"
            : "Adjuntar la captura del mensaje"
        }
        onQuitar={(id) => {
          setPendientes(pendientes.filter((p) => p.id !== id))
          leer.reset()
        }}
      />
      <label className="block space-y-1" htmlFor="lector-contexto">
        <span className="text-sm font-medium">Contexto</span>
        <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
          {clase === "factura"
            ? "Lo que el papel no dice: cómo se pagó y qué es cada cosa. No cambia ninguna cifra impresa."
            : "A quién se paga, qué se paga, cuánto y en qué moneda. Si el mensaje dice otra cifra, no se crea."}
        </span>
        <textarea
          id="lector-contexto"
          value={contexto}
          onChange={(e) => setContexto(e.target.value)}
          maxLength={1000}
          rows={2}
          placeholder={
            clase === "factura"
              ? "Ej.: Makro, pagado desde Binance; las tazas son bien, lo demás consumible"
              : "Ej.: Mantenimiento de septiembre a Luis Alvarez, 110 $ por Binance"
          }
          className="campo mt-1 w-full"
        />
      </label>

      {/* Mientras lee, el progreso sustituye al botón y a la lectura anterior: es la
          única cosa que está pasando, y nada de lo de antes vale ya. */}
      {leer.isPending && <ProgresoLectura archivos={pendientes.length} />}

      {!lectura && !leer.isPending && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            La factura se envía a Google (Gemini) para leerla.
          </p>
          <Boton
            variante="primario"
            disabled={pendientes.length === 0}
            onClick={() => leer.mutate()}
          >
            <ScanText size={16} /> Leer con IA
          </Boton>
        </div>
      )}
      {leer.error instanceof ErrorApi && (
        <p className="text-sm" role="alert" style={{ color: "var(--destructive)" }}>
          {leer.error.message}
        </p>
      )}

      {lectura && !leer.isPending && (
        <ResultadoLectura
          // Una lectura nueva empieza sin ajustes: los de la anterior eran de otro papel.
          key={lectura.lectura_id}
          lectura={lectura}
          creando={crear.isPending}
          errorCrear={crear.error instanceof ErrorApi ? crear.error.message : null}
          onCrear={(ajustes, proveedorId) =>
            crear.mutate({ lecturaId: lectura.lectura_id, ajustes, proveedorId })
          }
          onReleer={() => {
            crear.reset()
            leer.mutate()
          }}
        />
      )}
    </div>
  )
}
